import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
import Youtube from '@tiptap/extension-youtube';
import Underline from '@tiptap/extension-underline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Image as ImageIcon, Link as LinkIcon, Video, Code, Heading1, Heading2, Heading3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const addImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addYouTubeVideo = () => {
    const url = prompt('Enter YouTube video URL:');
    if (url) {
      editor.commands.setYoutubeVideo({
        src: url,
        width: 640,
        height: 360,
      });
    }
  };

  const setLink = () => {
    const url = prompt('Enter link URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="border border-border rounded-sm rounded-b-none bg-card p-2 flex flex-wrap gap-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive('heading', { level: 1 }) ? 'bg-secondary' : ''}
        data-testid="editor-h1-button"
      >
        <Heading1 className="w-4 h-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editor.isActive('heading', { level: 2 }) ? 'bg-secondary' : ''}
        data-testid="editor-h2-button"
      >
        <Heading2 className="w-4 h-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={editor.isActive('heading', { level: 3 }) ? 'bg-secondary' : ''}
        data-testid="editor-h3-button"
      >
        <Heading3 className="w-4 h-4" aria-hidden="true" />
      </Button>
      <div className="w-px h-6 bg-border mx-1" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'bg-secondary' : ''}
        data-testid="editor-bold-button"
      >
        <Bold className="w-4 h-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'bg-secondary' : ''}
        data-testid="editor-italic-button"
      >
        <Italic className="w-4 h-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={editor.isActive('underline') ? 'bg-secondary' : ''}
        data-testid="editor-underline-button"
      >
        <UnderlineIcon className="w-4 h-4" aria-hidden="true" />
      </Button>
      <div className="w-px h-6 bg-border mx-1" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'bg-secondary' : ''}
        data-testid="editor-bullet-list-button"
      >
        <List className="w-4 h-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'bg-secondary' : ''}
        data-testid="editor-ordered-list-button"
      >
        <ListOrdered className="w-4 h-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={editor.isActive('codeBlock') ? 'bg-secondary' : ''}
        data-testid="editor-code-button"
      >
        <Code className="w-4 h-4" aria-hidden="true" />
      </Button>
      <div className="w-px h-6 bg-border mx-1" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={setLink}
        className={editor.isActive('link') ? 'bg-secondary' : ''}
        data-testid="editor-link-button"
      >
        <LinkIcon className="w-4 h-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addImage}
        data-testid="editor-image-button"
      >
        <ImageIcon className="w-4 h-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addYouTubeVideo}
        data-testid="editor-video-button"
      >
        <Video className="w-4 h-4" aria-hidden="true" />
      </Button>
    </div>
  );
};

const WikiEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('General');
  const [isMembersOnly, setIsMembersOnly] = useState(false);
  const [categories, setCategories] = useState(['General', 'Locations', 'Characters', 'History', 'Items', 'Lore']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEditMode = !!id;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Image,
      LinkExtension.configure({
        openOnClick: false,
      }),
      Youtube.configure({
        width: 640,
        height: 360,
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[300px] p-4 bg-input/50 border border-t-0 border-border rounded-sm rounded-t-none',
      },
    },
  });

  useEffect(() => {
    if (isEditMode) {
      fetchEntry();
    }
    fetchCategories();
  }, [id]);

  const fetchEntry = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/wiki/entries/${id}`, {
        withCredentials: true
      });
      setTitle(data.title);
      setCategory(data.category);
      setIsMembersOnly(data.is_members_only);
      if (editor) {
        editor.commands.setContent(data.content);
      }
    } catch (err) {
      setError('Error loading entry');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/wiki/categories`, {
        withCredentials: true
      });
      if (data.length > 0) {
        setCategories([...new Set([...categories, ...data])]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!editor) {
      setError('Editor not ready');
      setLoading(false);
      return;
    }

    try {
      const entryData = {
        title,
        content: editor.getHTML(),
        category,
        is_members_only: isMembersOnly
      };

      if (isEditMode) {
        await axios.put(
          `${BACKEND_URL}/api/wiki/entries/${id}`,
          entryData,
          { withCredentials: true }
        );
        navigate(`/wiki/${id}`);
      } else {
        const { data } = await axios.post(
          `${BACKEND_URL}/api/wiki/entries`,
          entryData,
          { withCredentials: true }
        );
        navigate(`/wiki/${data.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error saving entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] py-12 px-6" data-testid="wiki-editor-page">
      <div className="container mx-auto max-w-4xl">
        <Link to="/wiki" className="inline-flex items-center gap-2 text-primary hover:underline mb-8 font-sans" data-testid="editor-back-link">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Back to Glossary
        </Link>

        <Card className="bg-card border-border rounded-sm">
          <CardHeader>
            <CardTitle className="text-3xl font-serif text-foreground">
              {isEditMode ? 'Edit Glossary Entry' : 'Create New Glossary Entry'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title" className="font-sans text-foreground">Title</Label>
                <Input
                  id="title"
                  type="text"
                  placeholder="Entry title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  className="bg-input/50 border-border rounded-sm font-sans focus-visible:ring-primary"
                  data-testid="editor-title-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category" className="font-sans text-foreground">Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="bg-input/50 border-border rounded-sm font-sans" data-testid="editor-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border rounded-sm">
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat} className="font-sans">
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="members-only"
                      checked={isMembersOnly}
                      onCheckedChange={setIsMembersOnly}
                      className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      data-testid="editor-members-only-checkbox"
                    />
                    <Label
                      htmlFor="members-only"
                      className="font-sans text-foreground cursor-pointer"
                    >
                      Members Only
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-sans text-foreground">Content</Label>
                <p className="text-sm text-muted-foreground font-sans mb-2">
                  Use the toolbar to format text, insert images, and embed YouTube videos
                </p>
                <div data-testid="editor-content-field">
                  <MenuBar editor={editor} />
                  <EditorContent editor={editor} />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/50 rounded-sm" data-testid="editor-error-message">
                  <p className="text-sm text-destructive font-sans">{error}</p>
                </div>
              )}

              <div className="flex gap-4">
                <Button
                  type="submit"
                  className="font-sans rounded-sm"
                  disabled={loading}
                  data-testid="editor-submit-button"
                >
                  {loading ? 'Saving...' : (isEditMode ? 'Update Entry' : 'Create Entry')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/wiki')}
                  className="font-sans rounded-sm"
                  data-testid="editor-cancel-button"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WikiEditorPage;
